/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { factoryTBuilder, factoryT, fields } from 'factory-t';
import {
    CashBoxTaskItemDto,
    CashBoxTaskStatus,
    CashBoxTaskType,
    CreateCashBoxTaskResponseDto,
    RoughCashboxesListItemDto,
    DeviceServiceListItem,
} from './serviceClient.typings';
import { DeviceStatus, DeviceUSBType, DeviceWorkability } from '../devices/devicesClient.typing';
import { CASH_BOXES_STATUSES, CashBoxTypes } from '../cashBoxes/cashBoxesClient.typings';

export const roughCashboxesListItemFactory = factoryT<RoughCashboxesListItemDto>({
    taxNumber: (ctx) => `7323432${ctx.index}`,
    deviceFactoryNumber: fields.sequence(['43434343', null]),
    cashRegisterFactoryNumber: (ctx) => `23232${ctx.index}`,
    firmwareVersion: fields.sequence([
        '2.6.1-werw',
        '2.32.0',
        '2.20.0',
        null,
        '2.20-dfgdfgdf',
        '3.45',
        '3',
        '1.5',
        '2',
        '',
    ]),
    fiscalDriverVersion: fields.sequence(['1.6.1', '1.32.0', '1.5.4.ghfgh', '2.4', '']),

    status: fields.sequence([...CASH_BOXES_STATUSES]),

    cashBoxType: fields.sequence([CashBoxTypes.PHYSICAL, CashBoxTypes.SOFTWARE]),
    online: fields.sequence([true, true, true, false]),
    cashRegisterModelName: 'Model 0000000001',
    cashRegisterModelTaxNumber: (ctx) => `15500${ctx.index}`,
    softwareVersion: fields.sequence(['3.6.1', '3.32.0']),

    operatorTaxNumber: (ctx) => `453465${ctx.index}`,
    operatorName: fields.string(),
    companyTaxNumber: (ctx) => `8888${ctx.index}`,
    storeTaxNumber: (ctx) => `999${ctx.index}`,
});

export const createCashboxTaskResponseFactory = factoryT<CreateCashBoxTaskResponseDto>({
    //id: (ctx) => `1000${ctx.index}`,
    id: fields.sequence(['10001', null, '10003', '10004', null, null, null, '10006', '10007', '10008', '10009']),
    //id: null,
    cashBoxTaxNumber: (ctx) => `7323432${ctx.index}`,
    key: (ctx) => `key_${ctx.index}`,
    errorMessage: 'Error message, task rejected',
});

export const cashBoxCertUpdateTaskItemFactory = factoryT<CashBoxTaskItemDto>({
    cashBoxTaxNumber: (ctx) => `7323432${ctx.index}`,
    id: (ctx) => `1000${ctx.index}`,
    name: CashBoxTaskType.CERTUPDATE,
    status: fields.sequence([
        CashBoxTaskStatus.CREATED,
        CashBoxTaskStatus.FAILED,
        CashBoxTaskStatus.COMPLETED,
        CashBoxTaskStatus.COMPLETED,
        CashBoxTaskStatus.COMPLETED,
    ]),
    insertedAtUtc: '2020-01-09T11:27:11.996063',
    updatedAtUtc: '2020-02-03T11:27:11.996063',
    userName: 'Ivanov Ivan',
    statusCode: 1,
    statusDetails:
        'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa.',
    details: null,
    results: {},
});

export const cashBoxFwUpdateTaskItemFactory = factoryT<CashBoxTaskItemDto>({
    cashBoxTaxNumber: (ctx) => `7323432${ctx.index}`,
    id: (ctx) => `1000${ctx.index}`,
    name: CashBoxTaskType.FWUPDATE,
    status: fields.sequence([CashBoxTaskStatus.COMPLETED, CashBoxTaskStatus.FAILED, CashBoxTaskStatus.COMPLETED]),
    insertedAtUtc: '2020-01-09T11:27:11.996063',
    updatedAtUtc: '2020-02-03T11:27:11.996063',
    userName: 'Petrov Ivan',
    statusCode: 2,
    statusDetails:
        'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa.',
    details: { targetVersion: '2.0.0' },
    results: { currentVersion: '2.0.0' },
});

export const cashBoxLogsTaskItemFactory = factoryT<CashBoxTaskItemDto>({
    cashBoxTaxNumber: (ctx) => `7323432${ctx.index}`,
    id: (ctx) => `1000${ctx.index}`,
    name: CashBoxTaskType.LOGS,
    status: fields.sequence([CashBoxTaskStatus.CREATED, CashBoxTaskStatus.COMPLETED, CashBoxTaskStatus.FAILED]),
    insertedAtUtc: '2020-01-09T11:27:11.996063',
    updatedAtUtc: '2020-02-03T11:27:11.996063',
    userName: 'Sidorov Ivan',
    statusCode: 3,
    statusDetails: 'Lorem ipsum dolor sit amet, consectetuer adipiscing elit',
    details: null,
    results: undefined,
});

export const cashBoxEnvInfoTaskItemFactory = factoryT<CashBoxTaskItemDto>({
    cashBoxTaxNumber: (ctx) => `7323432${ctx.index}`,
    id: (ctx) => `1000${ctx.index}`,
    name: CashBoxTaskType.ENVINFO,
    status: fields.sequence([CashBoxTaskStatus.CREATED, CashBoxTaskStatus.FAILED, CashBoxTaskStatus.COMPLETED]),
    insertedAtUtc: '2020-01-09T11:27:11.996063',
    updatedAtUtc: '2020-02-03T11:27:11.996063',
    userName: 'Baranov Ivan',
    statusCode: 4,
    statusDetails:
        'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa.',
    details: undefined,
    results: {
        osName: 'Windows',
        osVersion: '10.0',
        osArch: 'x86',
        procCount: 8,
        ramSizeMB: 56765,
        utcOffsetSec: 342434534,
        fdVersion: '2.3.0',
    },
});

export const cashBoxChecksumTaskItemFactory = factoryT<CashBoxTaskItemDto>({
    cashBoxTaxNumber: (ctx) => `7323432${ctx.index}`,
    id: (ctx) => `1000${ctx.index}`,
    name: CashBoxTaskType.CHECKSUM,
    status: fields.sequence([CashBoxTaskStatus.CREATED, CashBoxTaskStatus.FAILED, CashBoxTaskStatus.COMPLETED]),
    insertedAtUtc: '2020-01-09T11:27:11.996063',
    updatedAtUtc: '2020-02-03T11:27:11.996063',
    userName: 'Pupkin Ivan',
    statusCode: 0,
    statusDetails: '',
    details: {},
    results: { checksum: '345sfjlsdjf4342wsfsfs' },
});

export const cashBoxDumpTaskItemFactory = factoryT<CashBoxTaskItemDto>({
    cashBoxTaxNumber: (ctx) => `7323432${ctx.index}`,
    id: (ctx) => `1000${ctx.index}`,
    name: CashBoxTaskType.DUMP,
    status: fields.sequence([CashBoxTaskStatus.FAILED, CashBoxTaskStatus.COMPLETED]),
    insertedAtUtc: '2020-01-09T11:27:11.996063',
    updatedAtUtc: '2020-02-03T11:27:11.996063',
    userName: 'Ivanov Ivan',
    statusCode: 5,
    statusDetails: 'ddd',
    details: null,
    results: {},
});

export const cashBoxRepairTaskItemFactory = factoryT<CashBoxTaskItemDto>({
    cashBoxTaxNumber: (ctx) => `7323432${ctx.index}`,
    id: (ctx) => `1000${ctx.index}`,
    name: CashBoxTaskType.REPAIR,
    status: fields.sequence([CashBoxTaskStatus.FAILED, CashBoxTaskStatus.COMPLETED]),
    insertedAtUtc: '2020-01-09T11:27:11.996063',
    updatedAtUtc: '2020-02-03T11:27:11.996063',
    userName: 'Ivanov Ivan',
    statusCode: 6,
    statusDetails:
        'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa.',
    details: null,
    results: {},
});

export const deviceDtoFactory = (factoryNumbers: string[]) =>
    factoryTBuilder<DeviceServiceListItem>({
        factoryNumber: (ctx) => factoryNumbers[ctx.index - 1],
        status: fields.sequence([
            DeviceStatus.LOST,
            DeviceStatus.FIXED,
            DeviceStatus.BROKEN,
            DeviceStatus.PRODUCED,
            DeviceStatus.STORE,
        ]),
        statusDate: 1615274301,
        statusComment: 'This is some status comment',
        usbType: fields.sequence([DeviceUSBType.microB, DeviceUSBType.microB, DeviceUSBType.A]),
        firmwareVersion: 'v1.43.2.0',
        wearout: fields.sequence([350000, 0, 4000000]),
        workability: fields.sequence([
            DeviceWorkability.NEW,
            DeviceWorkability.JUNIOR,
            DeviceWorkability.SENIOR,
            DeviceWorkability.VETERAN,
            DeviceWorkability.INVALID,
        ]),
        operatorTaxNumber: fields.sequence(['342342881', null, '2342424']),
        hardwareVersion: '1.1.0',
    }).factory();
